"use client";

import { IPropertyCard } from "@/models/interfaces";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const MAX_COMPARE = 2;
const STORAGE_KEY = "dwellfinder-compare";

export interface CompareItem extends IPropertyCard {
  /** Extra fields that come from the search/card data */
  description?: string;
  amenities?: string[];
  furnishingStatus?: boolean;
  petPolicy?: boolean;
  parking?: boolean;
  leaseTerms?: string;
  paymentType?: string;
  landArea?: string;
  category?: string | null;
  subcategory?: string | null;
  depositRequired?: boolean;
  availableFrom?: string;
}

interface CompareContextValue {
  /** Currently selected items (0–2) */
  items: CompareItem[];
  /** Add a listing to compare. Returns false if already at max. */
  addItem: (item: CompareItem) => boolean;
  /** Remove a listing by id */
  removeItem: (id: string) => void;
  /** Clear all items */
  clearAll: () => void;
  /** Check if a listing is already selected for comparison */
  isSelected: (id: string) => boolean;
  /** Whether we've hit the max */
  isFull: boolean;
}

const CompareContext = createContext<CompareContextValue>({
  items: [],
  addItem: () => false,
  removeItem: () => {},
  clearAll: () => {},
  isSelected: () => false,
  isFull: false,
});

function loadFromStorage(): CompareItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_COMPARE) : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: CompareItem[]) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // sessionStorage might be unavailable
  }
}

export function CompareProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CompareItem[]>([]);

  // Hydrate from sessionStorage on mount
  useEffect(() => {
    setItems(loadFromStorage());
  }, []);

  // Persist on change
  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  const addItem = useCallback(
    (item: CompareItem): boolean => {
      let added = false;
      setItems((prev) => {
        if (prev.length >= MAX_COMPARE) return prev;
        if (prev.some((p) => p.id === item.id)) return prev;
        added = true;
        return [...prev, item];
      });
      return added;
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
  }, []);

  const isSelected = useCallback(
    (id: string): boolean => items.some((p) => p.id === id),
    [items]
  );

  return (
    <CompareContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        clearAll,
        isSelected,
        isFull: items.length >= MAX_COMPARE,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within a CompareProvider");
  }
  return context;
}
